import { useMemo, useRef } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";

import { useKeywordsCampaignsContext } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsContext";
import { getAPIPrefixForRT } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsInfraUtils";
import type { AiTaggingStatusResponse } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/ai-tagging/api/tagging";
import { queryKeys } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/ai-tagging/queryKeys";
import { getKeywordsReportSortColumn } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/getKeywordsReportSortColumn";
import type { IKeywordsReportTableData } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsReportTypes";
import { hasPendingKeywordRows } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/utils/hasPendingKeywordRows";
import { getApiParams } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/shared/KeywordsReportUtils";
import type { RTRawParams } from "app/pages/digital-marketing/keywords-campaigns/reports/types";
import { RankTrackerQueryType } from "app/pages/digital-marketing/keywords-campaigns/shared/constants";
import { useRankTrackerQuery, useRTApiDuration } from "app/pages/digital-marketing/keywords-campaigns/shared/hooks";
import { createCampaignQueryKey } from "app/pages/digital-marketing/keywords-campaigns/shared/utils";
import { DefaultFetchService } from "app/services/fetchService";

const PENDING_SCRAPE_REFETCH_INTERVAL_MS = 5 * 60 * 1000; // 5 mins

const fetchService = DefaultFetchService.getInstance();

type UseKeywordsReportTableDataQueryParams = {
    page: number;
};

export const useKeywordsReportTableQuery = ({ page }: UseKeywordsReportTableDataQueryParams) => {
    const { currentCampaignConfiguration, updateCurrentCampaign } = useKeywordsCampaignsContext();
    const { params } = useCurrentStateAndParams<RTRawParams>();
    const {
        tableGranularity,
        sortedColumn,
        asc,
        useCaseFilter,
        sites,
        MainSitePositionTrend,
        compPosState,
        campaignId,
    } = params;
    const queryClient = useQueryClient();
    const durationObject = useRTApiDuration();
    const pollingActiveRef = useRef(false);

    const apiUrl = useMemo(() => {
        if (!currentCampaignConfiguration) {
            return undefined;
        }
        return `${getAPIPrefixForRT(currentCampaignConfiguration.isDemo)}/reports/keywordsReport/Table`;
    }, [currentCampaignConfiguration]);

    const apiParams = useMemo(() => {
        if (!currentCampaignConfiguration || !durationObject) {
            return undefined;
        }

        const sharedApiParams = getApiParams(
            durationObject,
            currentCampaignConfiguration,
            params,
            tableGranularity,
            sites,
        );
        const sortBy = sortedColumn?.split(":")?.[0];
        const ascValue = asc === "true";
        const newSortParams = getKeywordsReportSortColumn(sortBy, ascValue);

        return {
            ...sharedApiParams,
            ...newSortParams,
            includeTrend: false,
            asc: asc === "true",
            page,
            useCaseFilter,
            MainSitePositionTrend,
            competitorsPositionState: compPosState,
        };
    }, [
        currentCampaignConfiguration,
        durationObject,
        params,
        tableGranularity,
        sites,
        sortedColumn,
        asc,
        page,
        useCaseFilter,
        MainSitePositionTrend,
        compPosState,
    ]);

    const query = useRankTrackerQuery<IKeywordsReportTableData>({
        enabled:
            !!currentCampaignConfiguration &&
            currentCampaignConfiguration.id === campaignId &&
            !!apiUrl &&
            !!apiParams?.scrapingConfigurationGuids,
        queryKey: createCampaignQueryKey(currentCampaignConfiguration?.id, RankTrackerQueryType.KeywordsTable, [
            apiUrl,
            apiParams,
        ]),
        queryFn: async () => {
            const data = await fetchService.get<IKeywordsReportTableData>(apiUrl, apiParams);
            const lastScrapeDate = currentCampaignConfiguration?.lastScrapeDate ?? null;
            pollingActiveRef.current = hasPendingKeywordRows(data.records, durationObject, lastScrapeDate);

            if (currentCampaignConfiguration?.id === campaignId && pollingActiveRef.current) {
                // Re-fetch campaign to have its potentially updated lastScrapeDate
                void updateCurrentCampaign(true);
            }

            return data;
        },
        select: (data) => {
            const status = queryClient.getQueryData<AiTaggingStatusResponse>(queryKeys.status(campaignId));
            const isTaggingInProgress = status?.lastTaggingAttemptStatus === "InProgress";
            return {
                ...data,
                aiTagging: { isLoading: isTaggingInProgress },
            };
        },
        refetchInterval: () => (pollingActiveRef.current ? PENDING_SCRAPE_REFETCH_INTERVAL_MS : false),
    });

    return {
        ...query,
        apiUrl,
        apiParams,
    };
};
